import { inject, Injectable } from '@angular/core';
import { QueryParamService } from './lists/query-params';

const allowedFilterTypes = ['contentCulls'] as const;
export type FilterType = (typeof allowedFilterTypes)[number];

export type StorageType = 'GLOBAL' | 'COMMERCIAL';

@Injectable({
  providedIn: 'root',
})
export class FilterStorageService {
  private readonly queryParamService = inject(QueryParamService);

  private readonly storage: {
    [K in StorageType]: {
      [T in FilterType]: number[] | null;
    };
  } = {
    GLOBAL: { contentCulls: null },
    COMMERCIAL: { contentCulls: null },
  };

  public fetch(storageType: StorageType = 'GLOBAL'): void {
    for (const filterType of allowedFilterTypes) {
      const value = this.parse(filterType);
      const currentStoredValue = this.storage?.[storageType]?.[filterType] ?? null;
      // both values null or parsed value null, nothing to do. But if value exists then prefer storage over qp
      if (value) {
        this.storage[storageType][filterType] = currentStoredValue ?? value;
      }
    }
  }

  public set(storageType: StorageType, filterType: FilterType, value: number[]): void {
    const currentQueryParam = this.queryParamService.getQueryParam(filterType);
    const newValueStr = JSON.stringify(value);

    if (currentQueryParam === newValueStr) {
      return; // prevent unnecessary update which can cause loops
    }

    this.storage[storageType][filterType] = currentQueryParam ?? value;
    this.queryParamService.setQueryParam(filterType, newValueStr);
  }

  public get(storageType: StorageType, filterType: FilterType): number[] | null {
    const currentStoredValue = this.storage?.[storageType]?.[filterType] ?? null;
    if (!currentStoredValue || currentStoredValue.length === 0 || !Array.isArray(currentStoredValue)) {
      return null;
    }
    return currentStoredValue;
  }

  public clear(storageType: StorageType, filterType: FilterType): void {
    this.storage[storageType][filterType] = null;
    this.queryParamService.clearQueryParam(filterType);
  }

  private parse(filterType: FilterType): number[] | null {
    const value = this.queryParamService.getQueryParam(filterType);
    if (!value) {
      return null;
    }
    if (value === '' || value === 'null' || value === 'undefined') {
      this.queryParamService.clearQueryParam(filterType);
      return null;
    }
    try {
      return JSON.parse(value) as number[];
    } catch (e) {
      return null;
    }
  }
}
